import React, { useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import ArtEventDropdown from '../ArtEventDropdown';
import { StyledSideNav } from './LeftNavigationBar.styles';
import { NavigationItem } from './NavigationItem';
import {
  navigationConfig,
  NavigationItem as NavigationItemType,
} from '../../config/navigationConfig';
import { useAppContext } from '../../store/appContext';
import { SET_SELECTED_NAVIGATION_ID } from '../../store/actions/types';

export function LeftNavigationBar(): React.ReactElement {
  const location = useLocation();
  const { dispatch, state } = useAppContext();

  // Memoize filtered navigation to prevent infinite re-renders
  const filteredNavigation = useMemo(
    () =>
      navigationConfig.filter(
        (item) =>
          !item.requiredRoles ||
          (state.currentUser?.role &&
            item.requiredRoles.includes(state.currentUser.role))
      ),
    [state.currentUser?.role]
  );

  // Helper function to find navigation item by path
  const findNavigationItemByPath = useCallback(
    (items: NavigationItemType[], path: string): NavigationItemType | null => {
      for (const item of items) {
        // Check exact match first
        if (item.path === path) {
          return item;
        }

        // Check children for exact matches first
        if (item.children) {
          const childMatch = findNavigationItemByPath(item.children, path);
          if (childMatch) {
            return childMatch;
          }
        }

        // Then check if path starts with item path (for nested routes)
        if (path !== '/' && item.path !== '/' && path.startsWith(item.path)) {
          return item;
        }
      }
      return null;
    },
    []
  );

  // Sync navigation selection with current route
  useEffect(() => {
    const matchedItem = findNavigationItemByPath(
      filteredNavigation,
      location.pathname
    );
    if (matchedItem) {
      dispatch({
        type: SET_SELECTED_NAVIGATION_ID,
        payload: matchedItem.id,
      });
    }
  }, [
    location.pathname,
    dispatch,
    findNavigationItemByPath,
    filteredNavigation,
  ]);

  return (
    <StyledSideNav isInitiallyCollapsed={true}>
      <ArtEventDropdown />
      {filteredNavigation.map((item) => (
        <NavigationItem key={item.id} item={item} />
      ))}
    </StyledSideNav>
  );
}
